import { computed, signal } from '@preact/signals';
import {
  DEFAULT_FINANCE_LIMITS,
  type ConfigFaq,
  type FinanceLimits,
  type RateInputRequirements,
} from 'shared';
import { type ApiClient } from '@/lib/api-client.js';
import { bootErrorKind, type BootErrorKind } from '@/lib/error-copy.js';
import { NO_RATE_INPUT_REQUIREMENTS } from '@/lib/rate-inputs.js';

export interface Config {
  accountName: string;
  brandColor: string | null;
  websiteUrl: string | null;
  phone: string | null;
  rateInputs: RateInputRequirements;
  faqs: ConfigFaq[];
  financeLimits: FinanceLimits;
}

/** Which install path the account is on. Beside `Config` rather than in it, because it describes
    where the widget is mounted rather than the account it renders — `main.tsx` puts it on
    `sell-more:config` for the preview harness, and `embedded` below is the widget's own read. */
export interface Hosting {
  hosted: boolean;
  url: string | null;
}

/** Mirrors the `--sm-brand` fallback in index.css. */
export const DEFAULT_BRAND = '#0066cc';

/** One signal for one state, so "error kind implies error status" holds by construction. */
export type ConfigState =
  | { kind: 'loading' }
  | { kind: 'ready'; config: Config; hosting: Hosting }
  | { kind: 'error'; errorKind: BootErrorKind };

export const configState = signal<ConfigState>({ kind: 'loading' });

export const config = computed<Config | null>(() =>
  configState.value.kind === 'ready' ? configState.value.config : null,
);

/** One fallback for both rate call sites, so the entry form and a re-rate can't disagree. */
export const rateInputRequirements = computed<RateInputRequirements>(
  () => config.value?.rateInputs ?? NO_RATE_INPUT_REQUIREMENTS,
);

/** Empty until config resolves, so the FAQ section is simply absent during boot. */
export const faqs = computed<ConfigFaq[]>(() => config.value?.faqs ?? []);

/** Payment is only reachable after a rate, so the fallback never prices a real cart. */
export const financeLimits = computed<FinanceLimits>(
  () => config.value?.financeLimits ?? DEFAULT_FINANCE_LIMITS,
);

/** On a dealer's page, where `AppShell` supplies the band our hosted pages already have.
    False while config loads, which never shows — `App` renders the spinner until it resolves. */
export const embedded = computed<boolean>(() => {
  const state = configState.value;
  return state.kind === 'ready' && !state.hosting.hosted;
});

/** The raw pair; `dealerContactLink` applies the prefer-website-fall-back-to-phone policy. */
export const dealerContact = computed(() => ({
  websiteUrl: config.value?.websiteUrl ?? null,
  phone: config.value?.phone ?? null,
}));

export const dealerContactLink = computed<{ href: string; external: boolean } | null>(() => {
  const { websiteUrl, phone } = dealerContact.value;
  if (websiteUrl !== null) return { href: websiteUrl, external: true };
  if (phone !== null) return { href: `tel:${phone}`, external: false };
  return null;
});

let inFlight: Promise<Config> | null = null;

/** Concurrent callers share the in-flight promise; failure clears it so retry can re-issue. */
export function loadConfig(apiClient: ApiClient): Promise<Config> {
  const current = configState.value;
  if (current.kind === 'ready') return Promise.resolve(current.config);
  if (inFlight) return inFlight;

  configState.value = { kind: 'loading' };
  inFlight = (async () => {
    try {
      const res = await apiClient.config();
      // The fields the compat schema tolerates absent — see `ConfigResponseCompatSchema`. Absent
      // takes the value that renders nothing — no FAQ section, no hosted link — and the finance
      // limits take Budco's defaults, which checkout still checks against the account.
      const next: Config = {
        accountName: res.account_name,
        brandColor: res.brand_color,
        websiteUrl: res.website_url,
        phone: res.phone,
        rateInputs: res.rate_inputs,
        faqs: res.faqs ?? [],
        financeLimits: {
          minMonths: res.finance_min_months ?? DEFAULT_FINANCE_LIMITS.minMonths,
          maxMonths: res.finance_max_months ?? DEFAULT_FINANCE_LIMITS.maxMonths,
          minDownPaymentPct:
            res.finance_min_down_payment_pct ?? DEFAULT_FINANCE_LIMITS.minDownPaymentPct,
        },
      };
      const hosting: Hosting = { hosted: res.hosted ?? false, url: res.hosted_url ?? null };
      configState.value = { kind: 'ready', config: next, hosting };
      return next;
    } catch (err) {
      inFlight = null;
      configState.value = { kind: 'error', errorKind: bootErrorKind(err) };
      throw err;
    }
  })();

  return inFlight;
}

/** Invalidates the module-level cache so the demo sees fresh account data on remount. */
export function resetConfig(): void {
  configState.value = { kind: 'loading' };
  inFlight = null;
}
